import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Keyboard, Lightbulb, ListChecks, Rocket, Sparkles, X } from 'lucide-react';
import { dockPatchNotes, dockShortcuts, resolveDockContext } from './dockContent';

interface ForqyDockProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

/**
 * FORQY Dock — the contextual help/assistant region (spec 2.2). A right-hand dock that reads the
 * current route and offers curated help, a playbook, shortcuts and patch notes. The "ask FORQY"
 * field is intentionally inert (roadmap) so nothing implies an AI backend that does not exist yet.
 */
export function ForqyDock({ open, onClose }: ForqyDockProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const context = resolveDockContext(location.pathname);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="forq-dock"
      role="dialog"
      aria-modal="true"
      aria-label="FORQY – Hilfe und Assistent"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <aside className="forq-dock__panel">
        <header className="forq-dock__head">
          <div className="forq-dock__brand">
            <span className="forq-dock__mark"><Sparkles size={16} /></span>
            <div>
              <span className="forq-eyebrow">Hilfe & Assistent</span>
              <h2>FORQY</h2>
            </div>
          </div>
          <button type="button" className="forq-icon-button" onClick={onClose} aria-label="Schließen">
            <X size={16} />
          </button>
        </header>

        <div className="forq-dock__body">
          <section className="forq-dock__section forq-dock__context">
            <span className="forq-dock__here">Du bist hier</span>
            <h3>{context.area}</h3>
            <p>{context.summary}</p>
            <ul className="forq-dock__tips">
              {context.tips.map((tip) => (
                <li key={tip}><Lightbulb size={13} /><span>{tip}</span></li>
              ))}
            </ul>
          </section>

          {context.playbook && (
            <section className="forq-dock__section">
              <h4><ListChecks size={14} /> {context.playbook.title}</h4>
              <ol className="forq-dock__steps">
                {context.playbook.steps.map((step) => <li key={step}>{step}</li>)}
              </ol>
            </section>
          )}

          <section className="forq-dock__section">
            <h4><Keyboard size={14} /> Tastenkürzel</h4>
            <ul className="forq-dock__shortcuts">
              {dockShortcuts.map((shortcut) => (
                <li key={shortcut.label}>
                  <span className="forq-dock__keys">
                    {shortcut.keys.map((key) => <kbd key={key}>{key}</kbd>)}
                  </span>
                  <span>{shortcut.label}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="forq-dock__section">
            <h4><Rocket size={14} /> Neu in FORQ</h4>
            <ul className="forq-dock__notes">
              {dockPatchNotes.map((note) => (
                <li key={note.title}><strong>{note.title}</strong><span>{note.body}</span></li>
              ))}
            </ul>
          </section>
        </div>

        <footer className="forq-dock__foot">
          <div className="forq-dock__ask">
            <input type="text" disabled placeholder="FORQY fragen – bald verfügbar" aria-label="FORQY fragen (bald verfügbar)" />
            <span className="forq-dock__ask-hint">Sichere Antworten aus der FORQ-Wissensbasis folgen später.</span>
          </div>
          <button type="button" className="forq-dock__more" onClick={() => { onClose(); navigate('/forqy'); }}>
            Alle Hilfeartikel & Patch Notes <ArrowRight size={14} />
          </button>
        </footer>
      </aside>
    </div>
  );
}
